import { v4 } from '@lukeed/uuid';
import { isAnyArray } from 'is-any-array';
import { xIsMonotonic, xMaxValue, xMinValue } from 'ml-spectra-processing';
import { getMeasurementXY } from './util/getMeasurementXY';
import { getMeasurementsXY } from './util/getMeasurementsXY';
import { getNormalizedMeasurement } from './util/getNormalizedMeasurement';
/**
 * Class allowing to store and manipulate an analysis.
 * An analysis may contain one or more measurements that can be selected
 * based on their units.
 */
export class Analysis {
    id;
    label;
    measurementCallback;
    measurements;
    cache;
    constructor(options = {}) {
        this.id = options.id || v4();
        this.label = options.label || this.id;
        this.measurementCallback = options.measurementCallback;
        this.measurements = [];
        this.cache = { measurement: {}, measurements: {} };
    }
    /**
     * Add a measurement in the internal measurements variable.
     * @param variables
     * @param options
     */
    pushMeasurement(variables, options = {}) {
        this.measurements.push(standardizeData(variables, options, {
            measurementCallback: this.measurementCallback,
        }));
        this.cache = { measurement: {}, measurements: {} };
    }
    /**
     * Retrieve a MeasurementXY based on x/y units.
     * @param selector
     * @returns an object contaning x and y
     */
    getMeasurementXY(selector = {}) {
        const id = JSON.stringify(selector);
        if (!this.cache.measurement[id]) {
            const measurement = getMeasurementXY(this.measurements, selector);
            if (measurement) {
                this.cache.measurement[id] = measurement;
            }
        }
        return this.cache.measurement[id];
    }
    /**
     * Retrieve spectra matching selector
     * @param selector
     */
    getMeasurementsXY(selector = {}) {
        const id = JSON.stringify(selector);
        if (!this.cache.measurements[id]) {
            this.cache.measurements[id] = getMeasurementsXY(this.measurements, selector);
        }
        return this.cache.measurements[id];
    }
    /**
     * Retrieve a xy object.
     * @param selector
     * @returns Object containing x and y
     */
    getXY(selector = {}) {
        const measurement = this.getMeasurementXY(selector);
        if (!measurement)
            return undefined;
        return {
            x: measurement.variables.x.data,
            y: measurement.variables.y.data,
        };
    }
    /**
     * Returns the data object for specific x/y units with possibly some
     * normalization options.
     * @param options.selector.xUnits - // if undefined takes the first variable.
     * @param options.selector.yUnits - // if undefined takes the second variable.
     * @param options
     * @returns object containing x and y
     */
    getNormalizedMeasurement(options = {}) {
        const { normalization, selector } = options;
        const measurement = this.getMeasurementXY(selector);
        if (!measurement)
            return undefined;
        return getNormalizedMeasurement(measurement, normalization);
    }
    /**
     * @param options
     */
    getNormalizedMeasurements(options = {}) {
        const { normalization, selector } = options;
        const measurements = this.getMeasurementsXY(selector);
        if (measurements.length === 0)
            return [];
        const normalizedMeasurements = [];
        for (const measurement of measurements) {
            normalizedMeasurements.push(getNormalizedMeasurement(measurement, normalization));
        }
        return normalizedMeasurements;
    }
    /**
     * Returns the first measurement. This method could be improved in the future.
     * @returns
     */
    getFirstMeasurement() {
        return this.measurements[0];
    }
    /**
     * Returns the x axis label.
     * @param selector
     * @returns A string containing the x label.
     */
    getXLabel(selector) {
        return this.getMeasurementXY(selector)?.variables.x.label;
    }
    /**
     * Returns the y axis label.
     * @param selector
     */
    getYLabel(selector) {
        return this.getMeasurementXY(selector)?.variables.y.label;
    }
}
/**
 * Internal function that ensure the order of x / y array.
 * @param variables
 * @param options
 * @param analysisOptions
 */
function standardizeData(variables, options, analysisOptions) {
    const { meta = {}, dataType = '', title = '' } = options;
    const { measurementCallback } = analysisOptions;
    if (measurementCallback) {
        measurementCallback(variables);
    }
    const xVariable = variables.x;
    const yVariable = variables.y;
    if (!xVariable || !yVariable) {
        throw Error('A measurement must contain at least x and y variables');
    }
    if (!isAnyArray(xVariable.data) || !isAnyArray(yVariable.data)) {
        throw Error('x and y variables must contain an array data');
    }
    const x = xVariable.data;
    const reverse = x && x.length > 1 && x[0] > x[x.length - 1];
    for (const [key, variable] of Object.entries(variables)) {
        if (reverse)
            variable.data = variable.data.slice().reverse();
        variable.label = variable.label || key;
        if (variable.label.match(/^.*[([](?<units>.*)[)\]].*$/)) {
            const units = variable.label.replace(/^.*[([](?<units>.*)[)\]].*$/, '$<units>');
            if (!variable.units || variable.units === units) {
                variable.units = units;
                variable.label = variable.label.replace(/[([].*[)\]]/, '').trim();
            }
        }
        variable.min = xMinValue(variable.data);
        variable.max = xMaxValue(variable.data);
        variable.isMonotonic = xIsMonotonic(variable.data);
    }
    return {
        variables,
        title,
        dataType,
        meta,
        id: v4(), // todo we should be able to have predefined id
    };
}
//# sourceMappingURL=Analysis.js.map